import { useQueryClient } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { CopyIcon } from '../../../../aida/components/svgs/icons/CopyIcon';
import {
	AIAgentTemplateFilterProperty,
	AIAgentTemplateScope,
	AIChatRole,
	AIConversationStarter,
	AIConversationType,
	CapabilityResourceConfiguration,
	CustomSurveyCapability,
	EventRegistrationCapability,
	IAIAgentTemplate,
	IAIAgentTemplateAttachment,
	IAnonymousAccessSettings,
	MeetingCapability,
	WebSearchCapability,
} from '../../../../extViewmodels/sdk/models/AI';
import { MastheadPortalId } from '../../../../models/AdminModels';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { copyToClipboard } from '../../../../models/UiUtils';
import {
	useAdminAccountSuspenseQuery,
	useAIAgentTemplateFilterInfiniteSuspenseQuery,
	useInfiniteCustomSurveys,
	useInfiniteEventRegistrationSurveys,
	useSchedulerConfigQuery,
	useSchedulerConfigsQuery,
} from '../../../../queries';
import { AI_AGENT_TEMPLATES_QUERY_KEY, invalidateAIAgentTemplateFilter } from '../../../../queries/AIAgentTemplate';
import { useCreateAIAgentTemplateMutation } from '../../../../queries/AIAgentTemplate/useCreateAIAgentTemplateMutation';
import { useUpdateAIAgentTemplateMutation } from '../../../../queries/AIAgentTemplate/useUpdateAIAgentTemplateMutation';
import { useImageAttachmentMutation } from '../../../../queries/Attachments/useImageAttachmentMutation';
import { useLoadAllUsers } from '../../../../queries/User/useLoadAllUsers';
import {
	type AssistantEmbedPosition,
	getAssistantEmbedSnippet,
	getAssistantStarterUrl,
} from '../../../../utils/assistantUrlUtils';
import { AIAssistantDialog } from '../../../../web/components-v2/ai/AIAssistantDialog';
import { Button } from '../../../../web/components-v2/Button';
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
	ComboboxStatus,
	ComboboxTrigger,
} from '../../../../web/components-v2/Combobox';
import { Input } from '../../../../web/components-v2/Input';
import {
	InputGroupV2 as InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupTextarea,
} from '../../../../web/components-v2/InputGroup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components-v2/Select';
import { TextArea } from '../../../../web/components-v2/TextArea';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../../web/components/Breadcrumb';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../web/components/Popover';
import { Portal } from '../../../../web/components/Portal';
import { ChevronDown } from '../../../../web/components/svgs/icons/ChevronDown';
import { CodeIcon } from '../../../../web/components/svgs/icons/CodeIcon';
import { EyeIcon } from '../../../../web/components/svgs/icons/EyeIcon';
import { TrashIcon } from '../../../../web/components/svgs/icons/TrashIcon';
import { TabList, TabRoot, TabTrigger } from '../../../../web/components/Tabs';
import { Toggle } from '../../../../web/components/Toggle';
import { charmGray } from '../../../../web/styles/colors';
import { bs } from '../../../../web/styles/styles';
import { AIAgentTemplateAttachments } from '../../AIAgentTemplates/components/AIAgentTemplateAttachments';
import { availableModels } from '../../AIAgentTemplates/constants';
import { getAssistantNameFromTemplateName } from '../../AIAgentTemplates/utils';
import { AIAgentTemplateFormData } from '../../AIAgentTemplates/validation';
import { styles } from '../styles';
import { AssistantPreviewWidget } from './components/AssistantPreviewWidget';
import { AvatarImageUploader } from './components/AvatarImageUploader';
import { EmbeddedPreviewWidget } from './components/EmbeddedPreviewWidget';

const DEFAULT_ANONYMOUS_ACCESS: IAnonymousAccessSettings = {
	enabled: false,
	allowedOrigins: [],
};

const EMBED_POSITION_OPTIONS: { label: string; value: AssistantEmbedPosition }[] = [
	{ label: 'Bottom Right', value: 'bottom-right' },
	{ label: 'Bottom Left', value: 'bottom-left' },
];

const HTTP_PROTOCOL_REGEX = /^https?:\/\//i;

const parseHttpUrl = (value: string): URL | null => {
	try {
		const url = new URL(value);
		if ((url.protocol !== 'http:' && url.protocol !== 'https:') || !url.hostname) {
			return null;
		}
		return url;
	} catch {
		return null;
	}
};

const normalizeHttpUrl = (value: string): string => {
	const trimmed = value.trim();
	if (!trimmed) {
		return '';
	}

	const withProtocol = HTTP_PROTOCOL_REGEX.test(trimmed) ? trimmed : `https://${trimmed}`;
	const url = parseHttpUrl(withProtocol);
	if (!url) {
		return withProtocol;
	}

	const normalizedPath = url.pathname === '/' ? '' : url.pathname.replace(/\/+$/, '');
	return `${url.protocol}//${url.host}${normalizedPath}${url.search}${url.hash}`;
};

const isValidWebSearchDomain = (domain: string): boolean => {
	return parseHttpUrl(domain) !== null;
};

const isValidEmbeddedAllowedOrigin = (origin: string): boolean => {
	const url = parseHttpUrl(origin);
	if (!url || url.protocol !== 'https:') {
		return false;
	}

	const hasPath = url.pathname !== '' && url.pathname !== '/';
	return !hasPath && !url.search && !url.hash;
};

// Helper function to convert string to AIConversationStarter with seeded chatMessages
const stringToConversationStarter = (title: string): AIConversationStarter => ({
	title,
	subTitle: undefined,
	chatMessages: title.trim() ? [{ id: null, role: AIChatRole.User, text: title }] : [],
});

function getInitialFormDataFromTemplate(template: IAIAgentTemplate): AIAgentTemplateFormData {
	let capabilities = template.capabilities ?? [];
	const hasCustomSurveyCapability = capabilities.some(c => c._type === 'CustomSurveyCapability');
	if (!hasCustomSurveyCapability) {
		capabilities = [
			...capabilities,
			{
				_type: 'CustomSurveyCapability',
				resources: [{ resourceId: '', description: '' }],
			} as CustomSurveyCapability,
		];
	}
	const hasEventCapability = capabilities.some(c => c._type === 'EventRegistrationCapability');
	if (!hasEventCapability) {
		capabilities = [
			...capabilities,
			{
				_type: 'EventRegistrationCapability',
				includeAllUpcomingEvents: true,
				resources: [{ resourceId: '' }],
			} as EventRegistrationCapability,
		];
	}
	const hasMeetingCapability = capabilities.some(c => c._type === 'MeetingCapability');
	if (!hasMeetingCapability) {
		capabilities = [
			...capabilities,
			{
				_type: 'MeetingCapability',
				resources: [{ resourceId: '', description: '' }],
			} as MeetingCapability,
		];
	}
	const hasWebSearchCapability = capabilities.some(c => c._type === 'WebSearchCapability');
	if (!hasWebSearchCapability) {
		capabilities = [
			...capabilities,
			{
				_type: 'WebSearchCapability',
				allowedDomains: [''],
			} as WebSearchCapability,
		];
	}
	return {
		name: template.name,
		description: template.description || '',
		objectivePrompt: template.objectivePrompt || '',
		technicalPrompt: template.technicalPrompt || '',
		model: template.model || '',
		conversationType: template.conversationType || '',
		selectors: template.selectors || {},
		variables: template.variables || {},
		attachments: template.attachments || [],
		agentAvatarImage: template.agentAvatarImage ?? null,
		conversationStarters: template.conversationStarters ?? [],
		anonymousAccess: template.anonymousAccess ?? DEFAULT_ANONYMOUS_ACCESS,
		capabilities,
	};
}

interface MeetingResourceRowProps {
	index: number;
	resource: CapabilityResourceConfiguration;
	allUsers: Api.IUser[];
	isLoadingUsers: boolean;
	isSubmitting: boolean;
	canRemove: boolean;
	impersonationContext?: Api.IImpersonationContext;
	onMeetingChange: (index: number, config: Api.IMeetingConfigResponse | null) => void;
	onDescriptionChange: (index: number, description: string) => void;
	onRemove: (index: number) => void;
}

function MeetingResourceRow({
	index,
	resource,
	allUsers,
	isLoadingUsers,
	isSubmitting,
	canRemove,
	impersonationContext,
	onMeetingChange,
	onDescriptionChange,
	onRemove,
}: MeetingResourceRowProps) {
	// State for selected user
	const [selectedUser, setSelectedUser] = React.useState<Api.IUser | null>(null);
	const [isUserPopoverOpen, setIsUserPopoverOpen] = React.useState(false);

	// Fetch existing config if we have a resourceId (edit mode)
	const existingConfigQuery = useSchedulerConfigQuery({
		id: resource.resourceId,
		enabled: Boolean(resource.resourceId) && !selectedUser,
		impersonationContext,
	});

	// Fetch meeting configs for selected user
	const { data: userConfigsData, isLoading: isLoadingConfigs } = useSchedulerConfigsQuery({
		//userId: selectedUser?.id,
		enabled: Boolean(selectedUser),
		impersonationContext: selectedUser
			? { ...impersonationContext, user: { id: selectedUser.id } }
			: impersonationContext,
		pageSize: 100,
	});
	const userConfigs = userConfigsData?.values ?? [];

	// Initialize selectedUser from existing config's creator (edit mode)
	React.useEffect(() => {
		if (existingConfigQuery.data && !selectedUser) {
			const creatorId = existingConfigQuery.data.creator?.id;
			if (creatorId) {
				const creator = allUsers.find(u => u.id === creatorId);
				if (creator) {
					setSelectedUser(creator);
				}
			}
		}
	}, [existingConfigQuery.data, selectedUser, allUsers]);

	let selectedConfig: Api.IMeetingConfigResponse = null;
	if (resource.resourceId) {
		const fromUserConfigs = userConfigs.find(c => c.id === resource.resourceId);
		if (fromUserConfigs) {
			selectedConfig = fromUserConfigs;
		} else if (existingConfigQuery.data?.id === resource.resourceId) {
			selectedConfig = existingConfigQuery.data;
		}
	}

	// Get currently selected config (with fallback for edit mode when user not found)

	// User button label (with fallback to creator from config)
	const userButtonLabel = React.useMemo(() => {
		if (selectedUser) {
			return getDisplayName(selectedUser);
		}
		// Fallback: try to get name from existing config's creator
		if (existingConfigQuery.data?.creator) {
			const creator = existingConfigQuery.data.creator;
			if (creator.firstName || creator.lastName) {
				return `${creator.firstName ?? ''} ${creator.lastName ?? ''}`.trim();
			}
			if (creator.email) {
				return creator.email;
			}
		}
		return 'Select User';
	}, [selectedUser, existingConfigQuery.data]);

	// Status message for calendar combobox
	const statusMessage = React.useMemo(() => {
		if (!selectedUser) return 'Select a user first';
		if (isLoadingConfigs) return 'Loading calendars...';
		if (userConfigs.length === 0) return 'No calendars for this user';
		return `${userConfigs.length} calendar${userConfigs.length === 1 ? '' : 's'} available`;
	}, [selectedUser, isLoadingConfigs, userConfigs.length]);

	// Handle user change - clear selected config
	const handleUserChange = (user: Api.IUser | null) => {
		setSelectedUser(user);
		setIsUserPopoverOpen(false);
		// Clear the selected calendar when user changes
		onMeetingChange(index, null);
	};

	return (
		<div className={css(styles.surveyRow)}>
			<div className={css(styles.surveyRowHeader)}>
				<div className={css(styles.surveyCombobox)}>
					<Combobox
						items={userConfigs}
						value={selectedConfig}
						onValueChange={value => onMeetingChange(index, value as Api.IMeetingConfigResponse | null)}
						itemToStringLabel={config =>
							(config as Api.IMeetingConfigResponse)?.published?.name ??
							(config as Api.IMeetingConfigResponse)?.draft?.name ??
							''
						}
						isItemEqualToValue={(item, value) =>
							(item as Api.IMeetingConfigResponse)?.id === (value as Api.IMeetingConfigResponse)?.id
						}
					>
						<ComboboxInput
							placeholder={selectedUser ? 'Search calendars...' : 'Select a user first'}
							disabled={isSubmitting}
							showClear
						>
							<InputGroupAddon align='inlineStart'>
								<Combobox
									items={allUsers}
									value={selectedUser}
									onValueChange={user => handleUserChange(user as Api.IUser | null)}
									itemToStringLabel={(user: Api.IUser) => getDisplayName(user)}
									isItemEqualToValue={(item: Api.IUser, value: Api.IUser) => item.id === value.id}
									open={isUserPopoverOpen}
									onOpenChange={setIsUserPopoverOpen}
								>
									<ComboboxTrigger
										render={({ className, ...props }) => (
											<InputGroupButton variant='ghost' size='xs' disabled={isSubmitting} {...props}>
												{userButtonLabel}
												<ChevronDown width={12} height={12} />
											</InputGroupButton>
										)}
									/>
									<ComboboxContent style={{ minWidth: 360, overflowX: 'hidden' }}>
										<div className={css(bs.p2)}>
											<ComboboxInput placeholder='Search users...' showTrigger={false} />
										</div>
										<ComboboxStatus>{isLoadingUsers ? 'Loading users...' : `${allUsers.length} users`}</ComboboxStatus>
										<ComboboxList>
											{(user: Api.IUser) => (
												<ComboboxItem key={user.id} value={user}>
													<ComboboxItemRadioIndicator />
													{getDisplayName(user)}
												</ComboboxItem>
											)}
										</ComboboxList>
									</ComboboxContent>
								</Combobox>
							</InputGroupAddon>
						</ComboboxInput>
						<ComboboxContent>
							<ComboboxStatus>{statusMessage}</ComboboxStatus>
							<ComboboxList>
								{(config: Api.IMeetingConfigResponse) => (
									<ComboboxItem key={config.id} value={config}>
										<ComboboxItemRadioIndicator />
										{config.published?.name ?? config.draft?.name}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</div>
				<TextArea
					styles={[styles.textArea]}
					value={resource.description ?? ''}
					onChange={e => onDescriptionChange(index, e.target.value)}
					placeholder='Describe when this calendar should be used'
					disabled={isSubmitting}
				/>
			</div>
			{canRemove ? (
				<Button
					type='button'
					variant='ghost'
					onClick={() => onRemove(index)}
					disabled={isSubmitting}
					aria-label='Remove calendar'
				>
					<TrashIcon fillColor={charmGray} />
				</Button>
			) : null}
		</div>
	);
}

interface CustomerAssistantEditorProps {
	accountId: string;
}

export function CustomerAssistantEditor({ accountId }: CustomerAssistantEditorProps) {
	const { data: account } = useAdminAccountSuspenseQuery({ accountId });
	const impersonationContext: Api.IImpersonationContext = React.useMemo(() => ({ account }), [account]);
	const { data: aiAgentTemplateData } = useAIAgentTemplateFilterInfiniteSuspenseQuery({
		filter: {
			criteria: [
				{ property: AIAgentTemplateFilterProperty.Scope, value: AIAgentTemplateScope.Account },
				{ property: AIAgentTemplateFilterProperty.ConversationType, value: AIConversationType.Custom },
			],
		},
		impersonationContext,
	});

	const existingTemplate = aiAgentTemplateData.pages[0]?.values?.[0];
	const isCreateMode = !existingTemplate;
	const companyName = account.companyName;
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const { data, isLoading } = useInfiniteCustomSurveys({
		impersonationContext,
		pageSize: 500,
	});
	const surveys = data?.pages.flatMap(page => page.values) ?? [];
	const surveysStatusMessage = React.useMemo(() => {
		if (isLoading) return 'Loading surveys...';
		if (surveys.length === 0) return 'No surveys found';
		return `${surveys.length} survey${surveys.length === 1 ? '' : 's'} available`;
	}, [isLoading, surveys.length]);

	// Event Registration Surveys query
	const { data: eventData, isLoading: isLoadingEvents } = useInfiniteEventRegistrationSurveys({
		impersonationContext,
		pageSize: 500,
	});
	const eventSurveys = eventData?.pages.flatMap(page => page.values) ?? [];
	const eventSurveysStatusMessage = React.useMemo(() => {
		if (isLoadingEvents) return 'Loading event surveys...';
		if (eventSurveys.length === 0) return 'No event surveys found';
		return `${eventSurveys.length} event survey${eventSurveys.length === 1 ? '' : 's'} available`;
	}, [isLoadingEvents, eventSurveys.length]);

	// Load all users for meeting config user selection
	const { users: allUsers, isLoading: isLoadingUsers } = useLoadAllUsers({
		impersonationContext,
		pageSize: 500,
	});

	// Editor mode tabs
	const [editorMode, setEditorMode] = React.useState<'standalone' | 'embedded-widget'>('standalone');

	// State for test chat modal
	const [isTestChatOpen, setIsTestChatOpen] = React.useState(false);
	const [embedPosition, setEmbedPosition] = React.useState<AssistantEmbedPosition>('bottom-right');
	const [embedLauncherLabel, setEmbedLauncherLabel] = React.useState('Chat with our AI assistant');

	const [formData, setFormData] = React.useState<AIAgentTemplateFormData>(() => {
		if (!existingTemplate) {
			return {
				name: '',
				description: '',
				objectivePrompt: '',
				technicalPrompt: '',
				model: '',
				conversationType: Api.AIConversationType.Custom,
				selectors: {},
				variables: {},
				attachments: [],
				agentAvatarImage: null,
				conversationStarters: [],
				anonymousAccess: DEFAULT_ANONYMOUS_ACCESS,
				capabilities: [
					{
						_type: 'CustomSurveyCapability',
						resources: [{ resourceId: '', description: '' }],
					} as CustomSurveyCapability,
					{
						_type: 'EventRegistrationCapability',
						includeAllUpcomingEvents: true,
						resources: [{ resourceId: '' }],
					} as EventRegistrationCapability,
					{
						_type: 'MeetingCapability',
						resources: [{ resourceId: '', description: '' }],
					} as MeetingCapability,
					{
						_type: 'WebSearchCapability',
						allowedDomains: [
							account.preferences?.subverticalSettings?.subverticals?.find(x => x.toLowerCase().includes('medicare'))
								? 'https://www.medicare.gov'
								: '',
						],
					} as WebSearchCapability,
				],
			};
		}
		return getInitialFormDataFromTemplate(existingTemplate);
	});
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [formErrors, setFormErrors] = React.useState<Record<string, string>>({});

	// Reset form when template changes (e.g., after create succeeds and we switch to edit mode)
	React.useEffect(() => {
		if (existingTemplate) {
			setFormData(getInitialFormDataFromTemplate(existingTemplate));
		}
	}, [existingTemplate]);

	// Derive avatar preview URL from form data
	const avatarPreviewUrl = formData.agentAvatarImage?.url ?? null;

	// Derive conversation starter strings for UI display
	const conversationStarterStrings = React.useMemo(() => {
		const starters = formData.conversationStarters ?? [];
		if (starters.length === 0) return [''];
		return starters.map(s => s.title);
	}, [formData.conversationStarters]);

	// Image attachment mutation for avatar upload
	const imageAttachmentMutation = useImageAttachmentMutation({
		onSuccess: attachments => {
			if (attachments.length > 0) {
				setFormData(prev => ({ ...prev, agentAvatarImage: attachments[0] }));
			}
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	// Handle avatar image changes - upload immediately when file is selected
	const handleAvatarChange = React.useCallback(
		(file: File | null) => {
			if (file) {
				imageAttachmentMutation.mutate({
					files: [file],
					impersonationContext,
				});
			} else {
				// Soft delete - just remove reference from form data
				setFormData(prev => ({ ...prev, agentAvatarImage: null }));
			}
		},
		[imageAttachmentMutation, impersonationContext]
	);

	// Mutation hooks for API calls
	const updateMutation = useUpdateAIAgentTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [AI_AGENT_TEMPLATES_QUERY_KEY] });
			toast.success('Customer Assistant updated successfully');
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
		onSettled: () => {
			invalidateAIAgentTemplateFilter();
		},
	});

	const createMutation = useCreateAIAgentTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [AI_AGENT_TEMPLATES_QUERY_KEY] });
			toast.success('Customer Assistant created successfully');
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
		onSettled: () => {
			invalidateAIAgentTemplateFilter();
		},
	});

	// Simplified validation function (removed conversationType and technicalPrompt)
	const validateForm = () => {
		const errors: Record<string, string> = {};

		if (!formData.name.trim()) {
			errors.name = 'Name is required';
		} else if (formData.name.length > 255) {
			errors.name = 'Name must be less than 255 characters';
		}

		if (formData.description && formData.description.length > 1000) {
			errors.description = 'Description must be less than 1000 characters';
		}

		if (!formData.objectivePrompt.trim()) {
			errors.objectivePrompt = 'Instructions are required';
		} else if (formData.objectivePrompt.length > 110000) {
			errors.objectivePrompt = 'Instructions must be less than 110000 characters';
		}

		if (!formData.model.trim()) {
			errors.model = 'Model is required';
		}

		// Validate allowed origins when public access is enabled
		if (formData.anonymousAccess?.enabled) {
			const origins = formData.anonymousAccess.allowedOrigins ?? [];
			const normalizedOrigins = origins.map(normalizeHttpUrl).filter(origin => origin !== '');
			const invalidOrigins = normalizedOrigins.filter(origin => !isValidEmbeddedAllowedOrigin(origin));
			if (invalidOrigins.length > 0) {
				errors.allowedOrigins =
					'Each whitelisted URL must be a valid https origin (e.g. https://example.com) with no path, query, or hash';
			}
		}

		const webSearchCapability = (formData.capabilities ?? []).find(c => c._type === 'WebSearchCapability') as
			| WebSearchCapability
			| undefined;
		const normalizedWebSearchDomains = (webSearchCapability?.allowedDomains ?? [])
			.map(normalizeHttpUrl)
			.filter(domain => domain !== '');
		const invalidWebSearchDomains = normalizedWebSearchDomains.filter(domain => !isValidWebSearchDomain(domain));
		if (invalidWebSearchDomains.length > 0) {
			errors.webSearchDomains = 'Each Web Search URL must be valid and start with http:// or https://';
		}

		return errors;
	};

	// Form submission handler
	const onSubmit = async () => {
		setIsSubmitting(true);
		setFormErrors({});
		// Validate form
		const errors = validateForm();
		if (Object.keys(errors).length > 0) {
			setFormErrors(errors);
			setIsSubmitting(false);
			return;
		}

		// Clean form data before submission
		const cleanedFormData: Api.IAIAgentTemplateFormData = { ...formData, scope: Api.AIAgentTemplateScope.Account };

		// Clean anonymousAccess allowedOrigins - filter empty strings
		if (cleanedFormData.anonymousAccess) {
			const filteredOrigins = (cleanedFormData.anonymousAccess.allowedOrigins ?? [])
				.map(normalizeHttpUrl)
				.filter(origin => origin !== '');
			cleanedFormData.anonymousAccess = {
				...cleanedFormData.anonymousAccess,
				allowedOrigins: filteredOrigins,
			};
		}

		// Clean capabilities
		if (cleanedFormData.capabilities) {
			// Step 1: Filter empty entries within each capability
			const cleanedCapabilities = cleanedFormData.capabilities
				.map(capability => {
					if (capability._type === 'WebSearchCapability') {
						const webCap = capability as WebSearchCapability;
						const filteredDomains = (webCap.allowedDomains ?? []).map(normalizeHttpUrl).filter(domain => domain !== '');
						return {
							...webCap,
							allowedDomains: filteredDomains,
						};
					} else if (
						capability._type === 'CustomSurveyCapability' ||
						capability._type === 'EventRegistrationCapability' ||
						capability._type === 'MeetingCapability'
					) {
						const resourceCap = capability as CustomSurveyCapability | EventRegistrationCapability | MeetingCapability;
						const filteredResources = (resourceCap.resources ?? []).filter(
							resource => resource.resourceId.trim() !== ''
						);
						return {
							...resourceCap,
							resources: filteredResources,
						};
					}
					return capability;
				})
				// Step 2: Filter out capabilities with empty arrays
				.filter(capability => {
					if (capability._type === 'WebSearchCapability') {
						const webCap = capability as WebSearchCapability;
						return (webCap.allowedDomains ?? []).length > 0;
					} else if (capability._type === 'EventRegistrationCapability') {
						const eventCap = capability as EventRegistrationCapability;
						// Keep if includeAllUpcomingEvents is true OR has resources
						return eventCap.includeAllUpcomingEvents || (eventCap.resources ?? []).length > 0;
					} else if (capability._type === 'CustomSurveyCapability' || capability._type === 'MeetingCapability') {
						const resourceCap = capability as CustomSurveyCapability | MeetingCapability;
						return (resourceCap.resources ?? []).length > 0;
					}
					return true; // Keep other capability types
				});

			cleanedFormData.capabilities = cleanedCapabilities;
		}

		try {
			if (isCreateMode) {
				await createMutation.mutateAsync({ data: cleanedFormData, impersonationContext });
			} else {
				// For updates, spread the original template data to preserve fields not in the form
				const updateData: AIAgentTemplateFormData = {
					...existingTemplate,
					...cleanedFormData,
				};
				await updateMutation.mutateAsync({
					id: existingTemplate!.id,
					data: updateData,
					impersonationContext,
				});
			}
		} catch (error) {
			errorMessages.pushApiError(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleFormSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		onSubmit();
	};

	const handleAttachmentsChange = (attachments: IAIAgentTemplateAttachment[]) => {
		setFormData(prev => ({ ...prev, attachments }));
	};

	const clearFormError = (field: string) => {
		setFormErrors(prev => {
			if (!prev[field]) {
				return prev;
			}

			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	// Conversation starters handlers
	const handleConversationStarterChange = (index: number, value: string) => {
		setFormData(prev => {
			const currentStarters = prev.conversationStarters ?? [];
			const updated = [...currentStarters];
			updated[index] = stringToConversationStarter(value);
			return { ...prev, conversationStarters: updated };
		});
	};

	const handleAddConversationStarter = () => {
		if (conversationStarterStrings.some(starter => starter.trim() === '')) {
			return;
		}
		setFormData(prev => ({
			...prev,
			conversationStarters: [...(prev.conversationStarters ?? []), stringToConversationStarter('')],
		}));
	};

	const handleRemoveConversationStarter = (index: number) => {
		setFormData(prev => ({
			...prev,
			conversationStarters: (prev.conversationStarters ?? []).filter((_, i) => i !== index),
		}));
	};

	// Anonymous access (Public Access) handlers
	const handlePublicAccessToggle = (enabled: boolean) => {
		clearFormError('allowedOrigins');
		setFormData(prev => ({
			...prev,
			anonymousAccess: {
				...prev.anonymousAccess,
				enabled,
				allowedOrigins: prev.anonymousAccess?.allowedOrigins ?? [],
			},
		}));
	};

	const handleAllowedOriginChange = (index: number, value: string) => {
		clearFormError('allowedOrigins');

		// If the list was empty we're using a virtual placeholder row;
		// ensure the origin actually gets persisted into state.
		if ((formData.anonymousAccess?.allowedOrigins?.length ?? 0) === 0) {
			setFormData(prev => ({
				...prev,
				anonymousAccess: {
					...prev.anonymousAccess,
					enabled: prev.anonymousAccess?.enabled ?? false,
					allowedOrigins: [value],
				},
			}));
		} else {
			setFormData(prev => {
				const currentOrigins = prev.anonymousAccess?.allowedOrigins ?? [];
				const updated = [...currentOrigins];
				updated[index] = value;
				return {
					...prev,
					anonymousAccess: {
						...prev.anonymousAccess,
						enabled: prev.anonymousAccess?.enabled ?? false,
						allowedOrigins: updated,
					},
				};
			});
		}
	};

	const handleAddAllowedOrigin = () => {
		setFormData(prev => ({
			...prev,
			anonymousAccess: {
				...prev.anonymousAccess,
				enabled: prev.anonymousAccess?.enabled ?? false,
				allowedOrigins: [...(prev.anonymousAccess?.allowedOrigins ?? []), ''],
			},
		}));
	};

	const handleRemoveAllowedOrigin = (index: number) => {
		clearFormError('allowedOrigins');
		setFormData(prev => ({
			...prev,
			anonymousAccess: {
				...prev.anonymousAccess,
				enabled: prev.anonymousAccess?.enabled ?? false,
				allowedOrigins: (prev.anonymousAccess?.allowedOrigins ?? []).filter((_, i) => i !== index),
			},
		}));
	};
	let surveyResources: CapabilityResourceConfiguration[] = [];
	if (formData.capabilities) {
		const surveyCapability = formData.capabilities.find(c => c._type === 'CustomSurveyCapability') as
			| CustomSurveyCapability
			| undefined;
		if (surveyCapability) {
			surveyResources = surveyCapability.resources ?? [];
		}
	}

	// Event Registration Survey resources
	let eventResources: CapabilityResourceConfiguration[] = [];
	let includeAllUpcomingEvents = true;
	if (formData.capabilities) {
		const eventCapability = formData.capabilities.find(c => c._type === 'EventRegistrationCapability') as
			| EventRegistrationCapability
			| undefined;
		if (eventCapability) {
			eventResources = eventCapability.resources ?? [];
			includeAllUpcomingEvents = eventCapability.includeAllUpcomingEvents ?? true;
		}
	}

	// Meeting resources
	let meetingResources: CapabilityResourceConfiguration[] = [];
	if (formData.capabilities) {
		const meetingCapability = formData.capabilities.find(c => c._type === 'MeetingCapability') as
			| MeetingCapability
			| undefined;
		if (meetingCapability) {
			meetingResources = meetingCapability.resources ?? [];
		}
	}

	// Web Search allowed domains
	let webSearchDomains: string[] = [];
	if (formData.capabilities) {
		const webSearchCapability = formData.capabilities.find(c => c._type === 'WebSearchCapability') as
			| WebSearchCapability
			| undefined;
		if (webSearchCapability) {
			webSearchDomains = webSearchCapability.allowedDomains ?? [''];
		}
	}
	if (webSearchDomains.length === 0) {
		webSearchDomains = [''];
	}

	const setSurveyResources = (resources: CapabilityResourceConfiguration[]) => {
		setFormData(prev => {
			return {
				...prev,
				capabilities: [
					...(prev.capabilities ?? []).filter(c => c._type !== 'CustomSurveyCapability'),
					{
						_type: 'CustomSurveyCapability',
						resources,
					} as CustomSurveyCapability,
				],
			};
		});
	};

	const setEventResources = (resources: CapabilityResourceConfiguration[]) => {
		setFormData(prev => {
			const existingCapability = prev.capabilities?.find(c => c._type === 'EventRegistrationCapability') as
				| EventRegistrationCapability
				| undefined;
			return {
				...prev,
				capabilities: [
					...(prev.capabilities ?? []).filter(c => c._type !== 'EventRegistrationCapability'),
					{
						_type: 'EventRegistrationCapability',
						includeAllUpcomingEvents: existingCapability?.includeAllUpcomingEvents ?? true,
						resources,
					} as EventRegistrationCapability,
				],
			};
		});
	};

	const setMeetingResources = (resources: CapabilityResourceConfiguration[]) => {
		setFormData(prev => {
			return {
				...prev,
				capabilities: [
					...(prev.capabilities ?? []).filter(c => c._type !== 'MeetingCapability'),
					{
						_type: 'MeetingCapability',
						resources,
					} as MeetingCapability,
				],
			};
		});
	};

	const setWebSearchDomains = (allowedDomains: string[]) => {
		setFormData(prev => {
			return {
				...prev,
				capabilities: [
					...(prev.capabilities ?? []).filter(c => c._type !== 'WebSearchCapability'),
					{
						_type: 'WebSearchCapability',
						allowedDomains,
					} as WebSearchCapability,
				],
			};
		});
	};

	const handleAddSurvey = () => {
		setSurveyResources([...surveyResources, { resourceId: '', description: '' }]);
	};
	const handleSurveyChange = (index: number, survey: Api.ICustomSurvey | null) => {
		const updated = [...surveyResources];
		updated[index] = {
			...updated[index],
			resourceId: survey?.id ?? '',
		};
		setSurveyResources(updated);
	};
	const handleDescriptionChange = (index: number, description: string) => {
		const updated = [...surveyResources];
		updated[index] = {
			...updated[index],
			description,
		};
		setSurveyResources(updated);
	};
	const handleRemoveSurvey = (index: number) => {
		setSurveyResources(surveyResources.filter((_, i) => i !== index));
	};

	// Event Survey handlers
	const handleAddEventSurvey = () => {
		setEventResources([...eventResources, { resourceId: '', description: '' }]);
	};
	const handleEventSurveyChange = (index: number, survey: Api.IEventRegistrationSurvey | null) => {
		const updated = [...eventResources];
		updated[index] = {
			...updated[index],
			resourceId: survey?.id ?? '',
		};
		setEventResources(updated);
	};
	const handleRemoveEventSurvey = (index: number) => {
		setEventResources(eventResources.filter((_, i) => i !== index));
	};

	const handleIncludeAllUpcomingEventsToggle = (enabled: boolean) => {
		setFormData(prev => {
			const existingCapability = prev.capabilities?.find(c => c._type === 'EventRegistrationCapability') as
				| EventRegistrationCapability
				| undefined;
			return {
				...prev,
				capabilities: [
					...(prev.capabilities ?? []).filter(c => c._type !== 'EventRegistrationCapability'),
					{
						_type: 'EventRegistrationCapability',
						includeAllUpcomingEvents: enabled,
						resources: existingCapability?.resources ?? [{ resourceId: '' }],
					} as EventRegistrationCapability,
				],
			};
		});
	};

	// Meeting config handlers
	const handleAddMeeting = () => {
		setMeetingResources([...meetingResources, { resourceId: '', description: '' }]);
	};
	const handleMeetingChange = (index: number, config: Api.IMeetingConfigResponse | null) => {
		const updated = [...meetingResources];
		updated[index] = {
			...updated[index],
			resourceId: config?.id ?? '',
		};
		setMeetingResources(updated);
	};
	const handleMeetingDescriptionChange = (index: number, description: string) => {
		const updated = [...meetingResources];
		updated[index] = {
			...updated[index],
			description,
		};
		setMeetingResources(updated);
	};
	const handleRemoveMeeting = (index: number) => {
		setMeetingResources(meetingResources.filter((_, i) => i !== index));
	};

	// Web Search handlers
	const handleAddWebsite = () => {
		setWebSearchDomains([...webSearchDomains, '']);
	};
	const handleWebsiteChange = (index: number, domain: string) => {
		clearFormError('webSearchDomains');
		const updated = [...webSearchDomains];
		updated[index] = domain;
		setWebSearchDomains(updated);
	};
	const handleRemoveWebsite = (index: number) => {
		clearFormError('webSearchDomains');
		setWebSearchDomains(webSearchDomains.filter((_, i) => i !== index));
	};

	const pageTitle = isCreateMode ? 'Create Customer Assistant' : `Edit Customer Assistant`;
	const embedSnippet = existingTemplate?.id
		? getAssistantEmbedSnippet({
				agentTemplateId: existingTemplate.id,
				position: embedPosition,
				launcherLabel: embedLauncherLabel,
			})
		: '';

	const handleCopyEmbedSnippet = async () => {
		if (!embedSnippet) {
			return;
		}
		const done = await copyToClipboard(embedSnippet);
		if (done) {
			toast.success('Embed snippet copied to clipboard.');
			return;
		}
		toast.error('Unable to copy to clipboard.');
	};

	return (
		<div className={css(styles.outerContainer)}>
			<Portal destination={MastheadPortalId}>
				<Breadcrumb>
					<BreadcrumbList>
						<BreadcrumbItem>
							<BreadcrumbLink asChild>
								<NavLink to='/accounts'>Accounts</NavLink>
							</BreadcrumbLink>
						</BreadcrumbItem>
						<BreadcrumbSeparator />
						<BreadcrumbItem>
							<BreadcrumbLink asChild>
								<NavLink to={`/accounts/${accountId}`}>{companyName ?? 'Loading...'}</NavLink>
							</BreadcrumbLink>
						</BreadcrumbItem>
						<BreadcrumbSeparator />
						<BreadcrumbItem>
							<BreadcrumbPage>Customer Assistant</BreadcrumbPage>
						</BreadcrumbItem>
					</BreadcrumbList>
				</Breadcrumb>
			</Portal>
			<div className={css(styles.twoColumnContainer)}>
				{/* Left Panel - Editor Form */}
				<form id='customer-assistant-form' onSubmit={handleFormSubmit} className={css(styles.editorPanel)}>
					<div className={css(styles.header)}>
						<h1 className={css(styles.title)}>{pageTitle}</h1>
						<div className={css(styles.actions)}>
							{!isCreateMode && existingTemplate?.id && (
								<Button
									type='button'
									size='sm'
									variant='secondary'
									disabled={!existingTemplate?.anonymousAccess?.enabled}
									onClick={() =>
										window.open(
											getAssistantStarterUrl({ agentTemplateId: existingTemplate.id }),
											'_blank',
											'noopener,noreferrer'
										)
									}
									title={!existingTemplate?.anonymousAccess?.enabled ? 'Enable public access to view' : undefined}
								>
									<EyeIcon fillColor='currentColor' />
									Preview
								</Button>
							)}
							{!isCreateMode && existingTemplate?.id && (
								<Popover>
									<PopoverTrigger asChild>
										<Button
											size='sm'
											type='button'
											variant='secondary'
											disabled={!existingTemplate?.anonymousAccess?.enabled}
											title={
												!existingTemplate?.anonymousAccess?.enabled
													? 'Enable public access to copy embed snippet'
													: undefined
											}
										>
											<CodeIcon />
											Embed
										</Button>
									</PopoverTrigger>
									<PopoverContent align='end' side='bottom' styles={[styles.embedPopover]}>
										<div className={css(styles.embedPopoverLayout)}>
											<div className={css(styles.embedOptionsContainer)}>
												<div className={css(styles.embedOptionGroup)}>
													<label className={css(styles.embedOptionLabel)}>Launcher position</label>
													<Select
														items={EMBED_POSITION_OPTIONS}
														value={embedPosition}
														onValueChange={value => setEmbedPosition(value as AssistantEmbedPosition)}
													>
														<SelectTrigger styles={[bs.wFull]}>
															<SelectValue placeholder='Select launcher position' />
														</SelectTrigger>
														<SelectContent>
															{EMBED_POSITION_OPTIONS.map(option => (
																<SelectItem key={option.value} value={option.value}>
																	{option.label}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</div>
												<div className={css(styles.embedOptionGroup)}>
													<label className={css(styles.embedOptionLabel)}>Launcher label</label>
													<Input
														type='text'
														value={embedLauncherLabel}
														onChange={event => setEmbedLauncherLabel(event.target.value)}
														disabled={isSubmitting}
													/>
												</div>
											</div>
											<div className={css(styles.embedOptionGroup)}>
												<label className={css(styles.embedOptionLabel)}>Embed script</label>
												<InputGroup>
													<InputGroupTextarea
														value={embedSnippet}
														readOnly
														onFocus={event => event.target.select()}
														styles={[styles.embedSnippetInput]}
													/>
												</InputGroup>
											</div>
											<Button
												type='button'
												variant='primary'
												size='sm'
												onClick={handleCopyEmbedSnippet}
												aria-label='Copy embed snippet'
												title='Copy embed snippet'
												styles={[styles.embedCopyButton]}
											>
												<CopyIcon fillColor='currentColor' /> Copy embed script
											</Button>
										</div>
									</PopoverContent>
								</Popover>
							)}
							<Button
								size='sm'
								type='submit'
								form='customer-assistant-form'
								disabled={isSubmitting || updateMutation.isPending || createMutation.isPending}
							>
								Save
							</Button>
						</div>
					</div>

					{/* Avatar Image Uploader */}
					<div className={css(styles.avatarUploaderContainer)}>
						<AvatarImageUploader
							previewUrl={avatarPreviewUrl}
							onImageChange={handleAvatarChange}
							disabled={isSubmitting}
							isUploading={imageAttachmentMutation.isPending}
						/>
						<div className={css(styles.assistantImageLabel)}>Assistant image</div>
					</div>

					{/* Name */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Name</label>
						<Input
							type='text'
							value={formData.name}
							onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
							placeholder='Enter assistant name'
							disabled={isSubmitting}
						/>
						{formErrors.name && <div className={css(styles.errorMessage)}>{formErrors.name}</div>}
					</div>

					{/* Description */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Description</label>
						<TextArea
							styles={[styles.textArea]}
							value={formData.description}
							onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
							placeholder='Enter assistant description'
							disabled={isSubmitting}
						/>
						{formErrors.description && <div className={css(styles.errorMessage)}>{formErrors.description}</div>}
					</div>

					{/* Model */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Model</label>
						<Select
							items={availableModels}
							value={formData.model || ''}
							onValueChange={value => setFormData(prev => ({ ...prev, model: value }))}
							disabled={isSubmitting}
						>
							<SelectTrigger styles={[bs.wFull]}>
								<SelectValue placeholder='Select AI model' />
							</SelectTrigger>
							<SelectContent>
								{availableModels.map(model => (
									<SelectItem key={model.value} value={model.value}>
										{model.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{formErrors.model && <div className={css(styles.errorMessage)}>{formErrors.model}</div>}
					</div>

					{/* Instructions (renamed from Objective Prompt) */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Instructions</label>
						<TextArea
							styles={[styles.textArea, styles.instructionsArea]}
							value={formData.objectivePrompt}
							onChange={e => setFormData(prev => ({ ...prev, objectivePrompt: e.target.value }))}
							placeholder='Enter instructions for the assistant'
							disabled={isSubmitting}
						/>
						{formErrors.objectivePrompt && <div className={css(styles.errorMessage)}>{formErrors.objectivePrompt}</div>}
					</div>

					{/* Conversation Starters */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Conversation Starters</label>
						<div className={css(styles.conversationStartersContainer)}>
							{conversationStarterStrings.map((starter, index) => (
								<div key={index} className={css(styles.conversationStarterRow)}>
									<Input
										type='text'
										value={starter}
										onChange={e => handleConversationStarterChange(index, e.target.value)}
										disabled={isSubmitting}
										styles={[styles.conversationStarterInput]}
									/>
									{conversationStarterStrings.length > 1 && (
										<Button
											type='button'
											variant='ghost'
											onClick={() => handleRemoveConversationStarter(index)}
											disabled={isSubmitting}
											aria-label='Remove conversation starter'
										>
											<TrashIcon fillColor={charmGray} />
										</Button>
									)}
								</div>
							))}
							<Button
								type='button'
								variant='link'
								onClick={handleAddConversationStarter}
								disabled={isSubmitting}
								styles={[styles.addConversationStarterLink]}
							>
								+ Add Conversation Starter
							</Button>
						</div>
					</div>

					{/* Public Access Toggle */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Public Access</label>
						<div className={css(styles.publicAccessToggleRow)}>
							<Toggle
								id='public-access-toggle'
								isOn={formData.anonymousAccess?.enabled ?? false}
								onToggleCheckChanged={handlePublicAccessToggle}
								disabled={isSubmitting}
							/>
							<span className={css(styles.publicAccessLabel)}>Enabled</span>
						</div>
					</div>
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Whitelisted host website URLs (For embedded mode)</label>
						<div className={css(styles.allowedOriginsContainer)}>
							{(formData.anonymousAccess?.allowedOrigins?.length ? formData.anonymousAccess.allowedOrigins : ['']).map(
								(origin, index) => (
									<div key={index} className={css(styles.allowedOriginRow)}>
										<Input
											type='text'
											value={origin}
											onChange={e => {
												handleAllowedOriginChange(index, e.target.value);
											}}
											placeholder='https://example.com'
											disabled={isSubmitting}
											styles={[styles.allowedOriginInput]}
										/>
										{(formData.anonymousAccess?.allowedOrigins?.length ?? 0) > 1 && (
											<Button
												type='button'
												variant='ghost'
												onClick={() => handleRemoveAllowedOrigin(index)}
												disabled={isSubmitting}
												aria-label='Remove whitelisted URL'
											>
												<TrashIcon fillColor={charmGray} />
											</Button>
										)}
									</div>
								)
							)}
							<Button
								type='button'
								variant='link'
								onClick={handleAddAllowedOrigin}
								disabled={isSubmitting}
								styles={[styles.addAllowedOriginLink]}
							>
								+ Add URL
							</Button>
							{formErrors.allowedOrigins && <div className={css(styles.errorMessage)}>{formErrors.allowedOrigins}</div>}
						</div>
					</div>
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Surveys</label>
						<div className={css(styles.surveyCapabilityContainer)}>
							{surveyResources.map((resource, index) => {
								const selectedSurvey = resource.resourceId
									? (surveys.find(s => s.id === resource.resourceId) ?? null)
									: null;

								return (
									<div key={index} className={css(styles.surveyRow)}>
										<div className={css(styles.surveyRowHeader)}>
											<div className={css(styles.surveyCombobox)}>
												<Combobox
													items={surveys}
													value={selectedSurvey}
													onValueChange={value => handleSurveyChange(index, value as Api.ICustomSurvey | null)}
													itemToStringLabel={survey => (survey as Api.ICustomSurvey)?.name ?? ''}
													isItemEqualToValue={(item, value) =>
														(item as Api.ICustomSurvey)?.id === (value as Api.ICustomSurvey)?.id
													}
												>
													<ComboboxInput placeholder='Search surveys...' disabled={isSubmitting} showClear />
													<ComboboxContent>
														<ComboboxStatus>{surveysStatusMessage}</ComboboxStatus>
														<ComboboxList>
															{(survey: Api.ICustomSurvey) => (
																<ComboboxItem key={survey.id} value={survey}>
																	<ComboboxItemRadioIndicator />
																	{survey.name}
																</ComboboxItem>
															)}
														</ComboboxList>
													</ComboboxContent>
												</Combobox>
											</div>
											<TextArea
												styles={[styles.textArea]}
												value={resource.description ?? ''}
												onChange={e => handleDescriptionChange(index, e.target.value)}
												placeholder='Describe when this survey should be used'
												disabled={isSubmitting}
											/>
										</div>
										{surveyResources.length > 1 ? (
											<Button
												type='button'
												variant='ghost'
												onClick={() => handleRemoveSurvey(index)}
												disabled={isSubmitting}
												aria-label='Remove survey'
											>
												<TrashIcon fillColor={charmGray} />
											</Button>
										) : null}
									</div>
								);
							})}
							<Button
								type='button'
								variant='link'
								onClick={handleAddSurvey}
								disabled={isSubmitting}
								styles={[styles.addSurveyLink]}
							>
								+ Add Survey
							</Button>
						</div>
					</div>

					{/* Event Surveys */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Event Surveys</label>
						<div className={css(styles.surveyCapabilityContainer)}>
							{/* Toggle for Include All Event Surveys */}
							<div className={css(styles.publicAccessToggleRow)}>
								<Toggle
									id='include-all-upcoming-events-toggle'
									isOn={includeAllUpcomingEvents}
									onToggleCheckChanged={handleIncludeAllUpcomingEventsToggle}
									disabled={isSubmitting}
								/>
								<span className={css(styles.publicAccessLabel)}>Include all event surveys</span>
							</div>
							<p className={css(styles.helpText)}>
								When enabled, the assistant will automatically have access to all non-archived future events.
							</p>

							{/* Only show individual event survey inputs when toggle is OFF */}
							{!includeAllUpcomingEvents && (
								<>
									{eventResources.map((resource, index) => {
										const selectedEventSurvey = resource.resourceId
											? (eventSurveys.find(s => s.id === resource.resourceId) ?? null)
											: null;

										return (
											<div key={index} className={css(styles.surveyRow)}>
												<div className={css(styles.surveyRowHeader)}>
													<div className={css(styles.surveyCombobox)}>
														<Combobox
															items={eventSurveys}
															value={selectedEventSurvey}
															onValueChange={value =>
																handleEventSurveyChange(index, value as Api.IEventRegistrationSurvey | null)
															}
															itemToStringLabel={survey => (survey as Api.IEventRegistrationSurvey)?.name ?? ''}
															isItemEqualToValue={(item, value) =>
																(item as Api.IEventRegistrationSurvey)?.id ===
																(value as Api.IEventRegistrationSurvey)?.id
															}
														>
															<ComboboxInput placeholder='Search event surveys...' disabled={isSubmitting} showClear />
															<ComboboxContent>
																<ComboboxStatus>{eventSurveysStatusMessage}</ComboboxStatus>
																<ComboboxList>
																	{(survey: Api.IEventRegistrationSurvey) => (
																		<ComboboxItem key={survey.id} value={survey}>
																			<ComboboxItemRadioIndicator />
																			{survey.name}
																		</ComboboxItem>
																	)}
																</ComboboxList>
															</ComboboxContent>
														</Combobox>
													</div>
												</div>
												{eventResources.length > 1 ? (
													<Button
														type='button'
														variant='ghost'
														onClick={() => handleRemoveEventSurvey(index)}
														disabled={isSubmitting}
														aria-label='Remove event survey'
													>
														<TrashIcon fillColor={charmGray} />
													</Button>
												) : null}
											</div>
										);
									})}
									<Button
										type='button'
										variant='link'
										onClick={handleAddEventSurvey}
										disabled={isSubmitting}
										styles={[styles.addSurveyLink]}
									>
										+ Add Event Survey
									</Button>
								</>
							)}
						</div>
					</div>

					{/* Meeting Configs */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Calendars for meeting booking</label>
						<div className={css(styles.surveyCapabilityContainer)}>
							{meetingResources.map((resource, index) => (
								<MeetingResourceRow
									key={index}
									index={index}
									resource={resource}
									allUsers={allUsers}
									isLoadingUsers={isLoadingUsers}
									isSubmitting={isSubmitting}
									canRemove={meetingResources.length > 1}
									impersonationContext={impersonationContext}
									onMeetingChange={handleMeetingChange}
									onDescriptionChange={handleMeetingDescriptionChange}
									onRemove={handleRemoveMeeting}
								/>
							))}
							<Button
								type='button'
								variant='link'
								onClick={handleAddMeeting}
								disabled={isSubmitting}
								styles={[styles.addSurveyLink]}
							>
								+ Add Calendar
							</Button>
						</div>
					</div>

					{/* Web Search */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Web Search</label>
						<div className={css(styles.surveyCapabilityContainer)}>
							{webSearchDomains.map((domain, index) => (
								<div key={index} className={css(styles.allowedOriginRow)}>
									<Input
										type='text'
										value={domain}
										onChange={e => handleWebsiteChange(index, e.target.value)}
										placeholder='Add a link to the AI knowledge base, such as https://www.clientcorp.com'
										disabled={isSubmitting}
										styles={[styles.allowedOriginInput]}
									/>
									{webSearchDomains.length > 1 && (
										<Button
											type='button'
											variant='ghost'
											onClick={() => handleRemoveWebsite(index)}
											disabled={isSubmitting}
											aria-label='Remove website'
										>
											<TrashIcon fillColor={charmGray} />
										</Button>
									)}
								</div>
							))}
							<Button
								type='button'
								variant='link'
								onClick={handleAddWebsite}
								disabled={isSubmitting}
								styles={[styles.addSurveyLink]}
							>
								+ Add Website
							</Button>
							{formErrors.webSearchDomains && (
								<div className={css(styles.errorMessage)}>{formErrors.webSearchDomains}</div>
							)}
						</div>
					</div>

					{/* Attachments */}
					<AIAgentTemplateAttachments
						attachments={formData.attachments}
						onChange={handleAttachmentsChange}
						templateId={!isCreateMode && existingTemplate ? existingTemplate.id : undefined}
					/>
				</form>

				{/* Right Panel - Preview */}
				<div className={css(styles.previewPanel)}>
					<TabRoot value={editorMode} onValueChange={value => setEditorMode(value as typeof editorMode)}>
						<TabList>
							<TabTrigger value='standalone'>Standalone</TabTrigger>
							<TabTrigger value='embedded-widget'>Embedded Widget</TabTrigger>
						</TabList>
					</TabRoot>
					{editorMode === 'standalone' ? (
						<AssistantPreviewWidget
							name={formData.name}
							description={formData.description}
							avatarUrl={avatarPreviewUrl}
							conversationStarters={conversationStarterStrings}
						/>
					) : (
						<EmbeddedPreviewWidget
							name={formData.name}
							description={formData.description}
							avatarUrl={avatarPreviewUrl}
							conversationStarters={conversationStarterStrings}
							position={embedPosition}
							launcherLabel={embedLauncherLabel}
						/>
					)}
				</div>

				{/* Test Chat Modal */}
				{existingTemplate && (
					<AIAssistantDialog
						open={isTestChatOpen}
						onOpenChange={setIsTestChatOpen}
						assistantName={getAssistantNameFromTemplateName(existingTemplate.name)}
						conversationType={
							(existingTemplate.conversationType as Api.AIConversationType) || Api.AIConversationType.SiteWide
						}
						agentTemplateId={existingTemplate.id}
						title={`Test: ${existingTemplate.name}`}
						initialMessage={`Hello! I'm the ${existingTemplate.name} agent. How can I help you today?`}
						placeholder='Send a test message...'
					/>
				)}
			</div>
		</div>
	);
}
